import dayjs from "dayjs";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Badge from "react-bootstrap/Badge";

import BackendCategoryAPI from "../../api/backend/category";
import OverlayLoading from "../../components/forms/OverlayLoading";
import BreadcrumbOne from "../../components/forms/BreadcrumbOne";
import LaravelPagination from "../../components/forms/LaravelPagination";
import TextBoxEditWithModal from "../../components/forms/TextBoxEditWithModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TextBoxCreateWithModal from "../../components/forms/TextBoxCreateWithModal";

const context = createContext();

const BackendCategoyPage = () => {
    const fetchParams = useRef({});

    const [data, setData] = useState({});
    const [paging, setPaging] = useState({ take: 10 });
    const [filter, setFilter] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchList = useCallback((payload) => {
        setLoading(() => true);
        BackendCategoryAPI.list(payload)
            .then((r) => setData(() => r.data))
            .finally(() => setLoading(() => false));
    }, []);

    const state = useMemo(() => {
        return {
            ...data,
            onChangePage: (value) => {
                setPaging((original) => ({ ...original, page: value }));
            },
            onChangeFilter: (values) => {
                setFilter((original) => ({ ...original, ...values }));
            },
            onCreateRecord: (values) => {
                setLoading(() => true);
                BackendCategoryAPI.create(values).finally(() => fetchList(fetchParams.current));
            },
            onDeleteRecord: (id) => {
                setLoading(() => true);
                BackendCategoryAPI.delete(id).finally(() => fetchList(fetchParams.current));
            },
            onChangeRecordProperty: (id, changes) => {
                setLoading(() => true);
                BackendCategoryAPI.update(id, changes).finally(() => fetchList(fetchParams.current));
            },
        };
    }, [fetchList, data]);

    useEffect(() => {
        fetchParams.current = { ...paging, filter: filter };
        fetchList(fetchParams.current);
    }, [fetchList, paging, filter]);

    return (
        <context.Provider value={state}>
            <div className="position-relative">
                <BreadcrumbOne name="จัดการหมวดหมู่" />

                <div className="mb-4">
                    <Header />
                </div>

                <div>
                    <Content />
                </div>

                <div>
                    <Footer />
                </div>

                {loading && <OverlayLoading />}
            </div>
        </context.Provider>
    );
};

const Header = () => {
    const mounted = useRef(false);
    const watchTimeout = useRef(null);

    const local = useContext(context);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (mounted.current) {
            clearTimeout(watchTimeout.current);
            watchTimeout.current = setTimeout(() => {
                local.onChangeFilter({ search });
            }, 300);
        }
    }, [search]);

    useEffect(() => {
        mounted.current = true;
    }, []);

    return (
        <Row>
            <Col sm={6} lg={4}>
                <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <Form.Control type="text" value={search} onChange={(e) => setSearch(e.target.value.trim())} />
                </Form.Group>
            </Col>
            <Col>
                <div className="d-flex gap-2 align-items-end justify-content-end h-100">
                    <TextBoxCreateWithModal onCreate={(v) => local.onCreateRecord?.({ name: v })} />
                </div>
            </Col>
        </Row>
    );
};

const Content = () => {
    const local = useContext(context);
    const data = local?.data ?? [];

    const onClickDelete = (row) => {
        confirm(`ต้องการลบ ${row.name} ใช่หรือไม่`) && local.onDeleteRecord?.(row.id);
    };

    const onChangeName = (row, changes) => {
        local.onChangeRecordProperty?.(row.id, { name: changes });
    };

    return (
        <Table responsive striped bordered hover style={{ verticalAlign: "middle" }} className="mb-0">
            <thead>
                <tr>
                    <th>#</th>
                    <th style={{ width: "100%", minWidth: 200, maxWidth: 200 }}>ชื่อหมวดหมู่</th>
                    <th style={{ width: 350, minWidth: 350, maxWidth: 350 }}>การใข้งาน</th>
                    <th style={{ width: 200, minWidth: 200, maxWidth: 200 }}>สร้างเมื่อ</th>
                    <th className="text-center" style={{ minWidth: 125, maxWidth: 125 }}>
                        การดำเนินการ
                    </th>
                </tr>
            </thead>
            <tbody>
                {data.map((v, i) => (
                    <tr key={i}>
                        <td>{v.id}</td>
                        <td>
                            <TextBoxEditWithModal value={v.name} onChange={(name) => onChangeName(v, name)} />
                        </td>
                        <td className="font-monospace">
                            <div className="d-flex gap-2">
                                {v.n1_exists && (
                                    <Badge bg="light" text="dark">
                                        N1
                                    </Badge>
                                )}

                                {v.n2_exists && (
                                    <Badge bg="light" text="dark">
                                        N2
                                    </Badge>
                                )}

                                {v.n3_exists && (
                                    <Badge bg="light" text="dark">
                                        N3
                                    </Badge>
                                )}

                                {v.n4_exists && (
                                    <Badge bg="light" text="dark">
                                        N4
                                    </Badge>
                                )}

                                {v.n5_exists && (
                                    <Badge bg="light" text="dark">
                                        N5
                                    </Badge>
                                )}

                                {v.n6_exists && (
                                    <Badge bg="light" text="dark">
                                        N6
                                    </Badge>
                                )}

                                {v.n7_exists && (
                                    <Badge bg="light" text="dark">
                                        N7
                                    </Badge>
                                )}
                            </div>
                        </td>
                        <td className="font-monospace">{v.created_at && dayjs(v.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                        <td className="text-center">
                            <button className="btn btn-sm btn-danger" onClick={() => onClickDelete(v)}>
                                <FontAwesomeIcon icon={["fas", "trash"]} />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
};

const Footer = () => {
    const local = useContext(context);

    return (
        <div className="d-flex align-items-center flex-wrap mt-3">
            <div>ทั้งหมด {local.total ?? 0} รายการ</div>
            <div className="flex-fill"></div>
            <LaravelPagination {...local} />
        </div>
    );
};

export default BackendCategoyPage;
